import React, { useState, useEffect, useRef } from 'react';
import './Flashcard.css';
import { getWordKey, getSRSStatus, useSRSStateWithGroupId, getGroupId } from '../utils/srs';
import { useLanguage } from '../contexts/LanguageContext';
import Skeleton from './Skeleton';
import { supabase } from '../supabaseClient';

const CheckIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="11" cy="11" r="11" fill="none" />
    <path d="M6 12.5L10 16L16 8" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const XIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="11" cy="11" r="11" fill="none" />
    <path d="M7 7L15 15M15 7L7 15" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const Flashcard = ({
  word, module, isFlipped, onFlip, moduleId, groupIdx,
  onFeedback, onSRSUpdate, userId, preloadedSRSData, loading, mode = 'sm2',
  sm2UndoCardIndex,
  onSm2RegisterUndo,
  learningUndoCardIndex,
  onLearningRegisterUndo
}) => {
  const { t } = useLanguage();
  const { srs, updateSRS } = useSRSStateWithGroupId(word, module, groupIdx, userId, preloadedSRSData, mode);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState('');
  /** Blocks overlapping SRS updates / tier jumps from rapid ✓/✗ clicks before the next card. */
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const feedbackInFlightRef = useRef(false);

  const languagePrefKey = `languagePref_${moduleId}`;
  const [frontSideMode, setFrontSideMode] = useState(() => {
    const saved = localStorage.getItem(languagePrefKey);
    if (saved === null) return 'from';
    try {
      const parsed = JSON.parse(saved);
      if (parsed === true) return 'from';
      if (parsed === false) return 'to';
      return ['from', 'to', 'random'].includes(parsed) ? parsed : 'from';
    } catch {
      return 'from';
    }
  });
  const [randomFromOnFront, setRandomFromOnFront] = useState(() => Math.random() < 0.5);

  const cycleFrontSideMode = () => {
    const nextMode = frontSideMode === 'from' ? 'to' : frontSideMode === 'to' ? 'random' : 'from';
    if (nextMode === 'random') setRandomFromOnFront(Math.random() < 0.5);
    setFrontSideMode(nextMode);
    localStorage.setItem(languagePrefKey, JSON.stringify(nextMode));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.flashcard-gear') && !event.target.closest('.flashcard-dropdown')) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  useEffect(() => {
    feedbackInFlightRef.current = false;
    setFeedbackBusy(false);
    if (frontSideMode === 'random') setRandomFromOnFront(Math.random() < 0.5);
  }, [word?.id, frontSideMode]);

  const handleFeedback = async (knewIt) => {
    if (feedbackInFlightRef.current) return;
    feedbackInFlightRef.current = true;
    setFeedbackBusy(true);
    setError('');
    try {
      // Create a promise for the minimum transition delay (400ms)
      const lingerPromise = new Promise(resolve => setTimeout(resolve, 400));

      const key = getWordKey(word, moduleId, getGroupId(module, groupIdx));
      const shouldRegisterSm2Undo =
        mode === 'sm2' &&
        typeof onSm2RegisterUndo === 'function' &&
        typeof sm2UndoCardIndex === 'number' &&
        userId &&
        word?.id;

      const shouldRegisterLearningUndo =
        mode === 'learning' &&
        typeof onLearningRegisterUndo === 'function' &&
        userId &&
        word?.id;

      const previousSrsSnapshot =
        shouldRegisterSm2Undo || shouldRegisterLearningUndo
          ? JSON.parse(JSON.stringify(srs))
          : null;

      // Start the SRS update. 
      // We pass `null` for the second arg (onSRSUpdate) to PREVENT it from being called immediately by the hook.
      // The hook will update its own local state (`setSRS`) optimistically, so the label changes instantly.
      const dbPromise = updateSRS(knewIt, null, mode);

      if (typeof onFeedback === 'function') {
        // Wait for BOTH the DB update to finish AND the 200ms linger time to pass
        const [updated] = await Promise.all([dbPromise, lingerPromise]);

        if (previousSrsSnapshot && shouldRegisterSm2Undo) {
          onSm2RegisterUndo({
            wordKey: key,
            wordId: word.id,
            previousSrs: previousSrsSnapshot,
            cardIndex: sm2UndoCardIndex
          });
        } else if (previousSrsSnapshot && shouldRegisterLearningUndo) {
          const payload = {
            wordKey: key,
            wordId: word.id,
            previousSrs: previousSrsSnapshot
          };
          if (typeof learningUndoCardIndex === 'number' && learningUndoCardIndex >= 0) {
            payload.cardIndex = learningUndoCardIndex;
          }
          onLearningRegisterUndo(payload);
        }

        // Now that it's time to advance, notify the parent and call onFeedback
        if (onSRSUpdate) onSRSUpdate(key, updated);
        onFeedback();
      } else {
        // If not advancing, just wait for the DB to finish
        const updated = await dbPromise;
        if (onSRSUpdate) onSRSUpdate(key, updated);
      }
    } catch (err) {
      setError(t('errorUpdatingProgress') || 'Failed to update your progress. Please try again.');
    } finally {
      feedbackInFlightRef.current = false;
      setFeedbackBusy(false);
    }
  };

  const rating = getSRSStatus(srs, t);

  // Use from_text and to_text for the new normalized structure
  const showFromOnFront = frontSideMode === 'from' || (frontSideMode === 'random' && randomFromOnFront);
  const frontContent = showFromOnFront ? word.from_text : word.to_text;
  const backContent = showFromOnFront ? word.to_text : word.from_text;
  const fromSideLabel = module.from_lang === module.to_lang ? t('term') : t(module.from_lang);
  const toSideLabel = module.from_lang === module.to_lang ? t('definition') : t(module.to_lang);
  const frontModeLabel = frontSideMode === 'from'
    ? `${fromSideLabel} ${t('onFront')}`
    : frontSideMode === 'to'
      ? `${toSideLabel} ${t('onFront')}`
      : t('randomSideOnFront');

  if (loading) {
    return (
      <div className="flashcard-container">
        <div className="flashcard">
          <Skeleton width="100%" height="3.5em" style={{ marginBottom: 16 }} />
          <Skeleton width="80%" height="2em" style={{ margin: '0 auto' }} />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flashcard-container" style={{ position: 'relative' }}>
        {error && (
          <div className="error-message" style={{ color: 'red', margin: '0.5rem 0', textAlign: 'center' }}>
            {error}
          </div>
        )}
        <div
          className={`flashcard ${isFlipped ? 'is-flipped' : ''}`}
          onClick={onFlip}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onFlip();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={isFlipped ? t('flipCardToFront') : t('flipCardToBack')}
        >
          <div className="flashcard-face flashcard-face-front">{frontContent}</div>
          <div className="flashcard-face flashcard-face-back">{backContent}</div>
        </div>

        <button
          className="flashcard-gear"
          onClick={(e) => { e.stopPropagation(); setShowDropdown(!showDropdown); }}
          aria-label={t('flashcardSettings')}
          aria-expanded={showDropdown}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M19.43 12.98c.04-.32.07-.65.07-.98s-.03-.66-.07-.98l2.11-1.65a.5.5 0 0 0 .12-.64l-2-3.46a.5.5 0 0 0-.6-.22l-2.49 1a7.03 7.03 0 0 0-1.7-.98l-.38-2.65A.488.488 0 0 0 14 2h-4a.5.5 0 0 0-.5.42l-.38 2.65c-.63.24-1.21.56-1.77.94l-2.49-1a.5.5 0 0 0-.6.22l-2 3.46a.5.5 0 0 0 .12.64l2.11 1.65c-.05.33-.08.66-.08 1s.03.67.08 1l-2.11 1.65a.5.5 0 0 0-.12.64l2 3.46c.14.24.44.33.68.22l2.49-1c.53.38 1.1.7 1.7.98l.38 2.65c.05.28.28.48.5.48h4c.28 0 .45-.2.5-.48l.38-2.65c.6-.28 1.17-.6 1.7-.98l2.49 1c.24.11.54.02.68-.22l2-3.46a.5.5 0 0 0-.12-.64l-2.11-1.65zM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7z" fill="currentColor" />
          </svg>
        </button>

        {showDropdown && (
          <div className="flashcard-dropdown" onClick={(e) => e.stopPropagation()}>
            <button className="dropdown-item front-side-mode-button" onClick={cycleFrontSideMode}>
              {frontModeLabel}
            </button>
            <button
              className="dropdown-item"
              onClick={async () => {

                const resetData = {
                  word_id: word.id,
                  user_id: userId,
                  sm2_ease: 2.5,
                  sm2_interval: 0,
                  learning_interval: 0,
                  next_due: Date.now(),
                  apparent_tier: 1,
                  history: [],
                  updated_at: new Date().toISOString()
                };

                // Update word_srs_data table
                if (userId && word.id) {
                  try {
                    const { error } = await supabase
                      .from('word_srs_data')
                      .upsert(resetData, {
                        onConflict: 'word_id,user_id'
                      });

                    if (error) throw error;

                    if (onSRSUpdate) {
                      const groupId = getGroupId(module, groupIdx);
                      const key = getWordKey(word, moduleId, groupId);

                      onSRSUpdate(key, {
                        sm2_interval: resetData.sm2_interval,
                        learning_interval: resetData.learning_interval,
                        ease: 2.5,
                        nextDue: resetData.next_due,
                        apparentTier: resetData.apparent_tier,
                        history: resetData.history,
                        lastUpdated: Date.now()
                      });
                    }
                    setShowDropdown(false);

                  } catch (err) {
                    console.error('Error resetting word progress:', err);
                    setError(t('errorUpdatingProgress') || 'Failed to update your progress. Please try again.');
                  }
                }
              }}
            >
              {t('resetProgress')}
            </button>
            <button
              className="dropdown-item"
              onClick={async () => {

                // Update learning_interval, apparent_tier, and add to history
                const currentHistory = word.history 
                  ? (typeof word.history === 'string' ? JSON.parse(word.history) : word.history)
                  : [];
                const masteredData = {
                  word_id: word.id,
                  user_id: userId,
                  learning_interval: 10,
                  apparent_tier: 5,
                  history: [...currentHistory, { result: 1, time: Date.now() }],
                  updated_at: new Date().toISOString()
                };

                // Update word_srs_data table
                if (userId && word.id) {
                  try {
                    const { error } = await supabase
                      .from('word_srs_data')
                      .upsert(masteredData, {
                        onConflict: 'word_id,user_id'
                      });

                    if (error) throw error;

                    if (onSRSUpdate) {
                      const groupId = getGroupId(module, groupIdx);
                      const key = getWordKey(word, moduleId, groupId);

                      onSRSUpdate(key, {
                        learning_interval: masteredData.learning_interval,
                        apparentTier: masteredData.apparent_tier,
                        history: masteredData.history,
                        // Preserve existing SRS data
                        ease: word.sm2_ease ?? 2.5,
                        sm2_interval: word.sm2_interval ?? 0,
                        nextDue: word.next_due ?? 0,
                        lastUpdated: Date.now()
                      });
                    }
                    setShowDropdown(false);

                  } catch (err) {
                    console.error('Error marking word as mastered:', err);
                    setError(t('errorUpdatingProgress') || 'Failed to update your progress. Please try again.');
                  }
                }
              }}
            >
              {t('markAsMastered')}
            </button>
            <button className="dropdown-item" onClick={() => setShowDropdown(false)}>
              {t('cancel')}
            </button>
          </div>
        )}
      </div>

      <div className="flashcard-feedback-row" aria-busy={feedbackBusy}>
        <button
          type="button"
          className="flashcard-feedback-btn x"
          disabled={feedbackBusy}
          onClick={e => { e.stopPropagation(); handleFeedback(false); }}
          title={t('iDidntKnowIt')}
          aria-label={t('iDidntKnowIt')}
        >
          <XIcon />
        </button>
        <button
          type="button"
          className="flashcard-feedback-btn check"
          disabled={feedbackBusy}
          onClick={e => { e.stopPropagation(); handleFeedback(true); }}
          title={t('iKnewIt')}
          aria-label={t('iKnewIt')}
        >
          <CheckIcon />
        </button>
      </div>

      <div className="knowledge-indicator">
        <span className="knowledge-dot" style={{ background: rating.color }}></span>
        <span style={{ color: rating.color }}>{rating.label}</span>
      </div>
    </>
  );
};

export default Flashcard;
